import axiosInstance from '../plugins/axiosInstance';

// ---- Debounce + AbortController za autocomplete (po prefixu) ----
const debounceTimers = new Map();
const aborters = new Map();

function prefixKey(text) {
  const t = (text || '').trim().toLowerCase();
  return t.slice(0, Math.max(3, Math.min(5, t.length))); // stabilen ključ za prefix (3–5 znakov)
}

/**
 * PlanPlus V2 Autocomplete (uporablja novi API endpoint)
 * Now supports optional coords for better ranking.
 * @param {string} text - Iskalni tekst
 * @param {{lat:number,lng:number}=} coords - Optional coords
 * @returns {Promise<Array>} Suggestions ali prazen array
 */
export async function fetchAddressSuggestionsPlanPlusV2(text, coords) {
  try {
    console.log(`[Frontend] PlanPlusV2 Autocomplete request: "${text}"`);

    const params = { text };
    if (coords?.lat && coords?.lng) {
      params.lat = coords.lat;
      params.lng = coords.lng;
    }

    const response = await axiosInstance.get('/misc/autocomplete-v2', { params });

    console.log(`[Frontend] PlanPlusV2 Found ${response.data.results?.length || 0} suggestions`);
    return response.data.results || [];
  } catch (error) {
    console.error('[Frontend] PlanPlusV2 Autocomplete error:', error);
    return [];
  }
}

/**
 * PlanPlus V2 Location Details (uporablja novi API endpoint)
 * @param {string} token - Token iz autocomplete rezultata
 * @returns {Promise<Object|null>} Location details ali null
 */
export async function fetchLocationDetailsPlanPlusV2(token) {
  try {
    console.log(`[Frontend] PlanPlusV2 Fetching location details for token: ${token}`);

    const response = await axiosInstance.get('/misc/location-details-v2', {
      params: { token },
    });

    if (response.data.success && response.data.data) {
      console.log('[Frontend] PlanPlusV2 Location details received:', response.data.data);
      return response.data.data;
    }

    console.error('[Frontend] PlanPlusV2 Location details error:', response.data.error);
    return null;
  } catch (error) {
    console.error('[Frontend] PlanPlusV2 Fetch location details error:', error);
    return null;
  }
}

/**
 * PlanPlus V2 Street Details (new endpoint)
 * Resolves precise lat/lng for a street suggestion using a house number.
 * @param {string} token
 * @param {string|number} houseNumber
 * @returns {Promise<Object|null>}
 */
export async function fetchStreetDetailsPlanPlusV2(token, houseNumber) {
  try {
    console.log(`[Frontend] PlanPlusV2 Fetching street details for token: ${token}, houseNumber: ${houseNumber}`);

    const response = await axiosInstance.get('/misc/street-v2', {
      params: { token, houseNumber },
    });

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('[Frontend] PlanPlusV2 Street details error:', error);
    return null;
  }
}

/**
 * Stara funkcija - uporablja fetch 
 * DEPRECATED: Uporabi fetchAddressSuggestionsPlanPlusV2 namesto tega
 */
export async function fetchAddressSuggestions(text, apiType) {
  try {
    console.log(`[Frontend] Autocomplete request: "${text}" (${apiType})`);

    let url;

    // PlanPlus V2 API
    if (apiType === 'Planplus') {
      url = `/misc/autocomplete-v2?text=${encodeURIComponent(text)}`;
    } 
    // Geoapify API (stari endpoint)
    else {
      url = `/misc/autocomplete?text=${encodeURIComponent(text)}&searchAddressApi=Geoapify`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      console.error('[Frontend] HTTP error:', response.status);
      return [];
    }

    const data = await response.json();

    console.log(`[Frontend] Found ${data.results?.length || 0} suggestions`);
    return data.results || [];
  } catch (error) {
    console.error('[Frontend] Fetch suggestions error:', error);
    return [];
  }
}

/**
 * NEDebounced osnovna funkcija (uporablja axios params + optional AbortSignal).
 * Dodan minimalni guard: min dolžina 2 znaka, da zmanjšamo anti-bot triggerje.
 */
export const fetchAddressSuggestionsMain = async (searchText, searchAddressApi, signal) => {
  const text = (searchText || '').trim();
  if (!text || text.length < 2) {
    return { lastSnp: null, autocompleteLocationObject: null, addressSuggestions: [] };
  }
  try {
    const response = await axiosInstance.get('/misc/autocomplete', {
      params: { text, searchAddressApi },
      signal, // omogoča cancel
    });
    const apiType = response && response.data ? response.data.apiType : undefined;
    switch (apiType) {
      case 'Planplus': {
        const responseObject = { lastSnp: { number: null }, autocompleteLocationObject: null, addressSuggestions: [] };
        responseObject.lastSnp = response.data.snp || { number: null };
        responseObject.autocompleteLocationObject = response.data && response.data.locationObject ? response.data.locationObject : null;
        const results = (response.data && Array.isArray(response.data.results)) ? response.data.results : [];
        responseObject.addressSuggestions = results.map((loc) => {
          const title = loc && loc.title ? loc.title : '';
          const address = loc && loc.address ? loc.address : '';
          const snpNumber = responseObject.lastSnp && responseObject.lastSnp.number ? ` ${responseObject.lastSnp.number}` : '';
          const label =
            title +
            snpNumber +
            (address ? (title ? `, ${address}` : `${address}`) : '');
          loc.apiType = apiType;
          return { label, value: loc };
        });
        return responseObject;
      }
      case 'Geoapify': {
        const responseObject = { lastSnp: null, autocompleteLocationObject: null, addressSuggestions: [] };
        responseObject.autocompleteLocationObject = response.data && response.data.locationObject ? response.data.locationObject : null;
        const results = (response.data && Array.isArray(response.data.results)) ? response.data.results : [];
        responseObject.addressSuggestions = results.map((loc) => {
          const title = loc && loc.title ? loc.title : '';
          const address = loc && loc.address ? loc.address : '';
          const label =
            title +
            (address ? (title ? `, ${address}` : `${address}`) : '');
          loc.apiType = apiType;
          return { label, value: loc };
        });
        return responseObject;
      }
      default:
        return { lastSnp: null, autocompleteLocationObject: null, addressSuggestions: [] };
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('fetchAddressSuggestionsMain error: ', e);
    }
    return { lastSnp: null, autocompleteLocationObject: null, addressSuggestions: [] };
  }
};

export const searchLocationPlanPlus = async (searchText) => {
  try {
    const response = await axiosInstance.get('/misc/searchLocationPlanPlus', {
      params: { text: searchText },
    });
    if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, '0')) {
      return response.data['0'];
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('searchLocationPlanPlus error: ', e);
    }
  }
};

export const getLocationObject = async (id, searchAddressApi) => {
  try {
    const response = await axiosInstance.get('/misc/locationObject', {
      params: { id, searchAddressApi },
    });
    return response ? response.data : undefined;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('getLocationObject error: ', e);
    }
  }
};

export const getStreetPlanPlus = async (id, number) => {
  try {
    const response = await axiosInstance.get('/misc/getStreetPlanPlus', {
      params: { id, number },
    });
    return response ? response.data : undefined;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('getStreetPlanPlus error: ', e);
    }
  }
};

/**
 * DEPRECATED:
 * Uporabi fetchLocationDetailsPlanPlusV2 namesto tega
 */
export async function fetchLocationDetailsPlanPlus(token) {
  try {
    console.log(`[Frontend] Fetching location details for token: ${token}`);

    const response = await fetch(
      `/misc/location-details-v2?token=${encodeURIComponent(token)}`
    );

    if (!response.ok) {
      console.error('[Frontend] HTTP error:', response.status);
      return null;
    }

    const data = await response.json();

    if (data.success && data.data) {
      console.log('[Frontend] Location details received:', data.data);
      return data.data;
    }

    console.error('[Frontend] PlanPlus location details error:', data.error);
    return null;
  } catch (error) {
    console.error('[Frontend] Fetch location details error:', error);
    return null;
  }
}